import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/** 제품소개 드롭다운 항목 (제품소개 페이지의 앵커 #s1~#s6 로 이동) */
const PRODUCT_MENU = [
  { hash: '#s1', title: '포탈 구성', desc: '만드는 사람도, 사용하는 사람도 편리하게' },
  { hash: '#s2', title: '지식 파이프라인', desc: '여러 곳에 있는 데이터를 AI가 참고할 지식으로' },
  { hash: '#s3', title: '워크플로우 빌더', desc: '반복되는 업무를 나만의 흐름으로' },
  { hash: '#s4', title: 'RAG 챗봇', desc: '사내 지식에서 찾고, 출처와 함께 답변' },
  { hash: '#s5', title: '라이브러리', desc: '내가 만든 결과물을 동료의 업무에도' },
  { hash: '#s6', title: '관리 기능', desc: 'AI를 만든 뒤 운영·관리까지 한곳에서' },
];

/**
 * 모바일 메뉴: 왼쪽 1차 메뉴(클릭 시 해당 페이지로 이동) / 오른쪽 하위 항목
 * - 하위 항목이 없는 메뉴는 이동 후 메뉴를 닫고, 있는 메뉴는 열어 둔 채 하위 항목을 보여 줌
 */
const MOBILE_MENU = [
  { key: '/', label: '홈', items: [] },
  { key: '/product', label: '제품소개', items: PRODUCT_MENU.map((m) => ({ to: `/product${m.hash}`, title: m.title })) },
  { key: '/contact', label: '문의하기', items: [] },
];

/**
 * 사이트 상단 메뉴
 * - 메뉴: 홈 / 제품소개(드롭다운) / 문의하기  (가격 페이지 없음)
 * - 우측: 로그인(/login, 일반 링크) / 문의하기(버튼) / 무료 체험(버튼, → 문의하기). 회원가입 없음
 */
export default function SiteHeader() {
  const { pathname } = useLocation();
  const on = (path: string) => (pathname === path ? 'on' : undefined);
  // 드롭다운을 클릭하면 닫아 두고, 마우스가 "제품소개" 로 다시 들어올 때 해제 (hover 로 계속 열려 있는 것 방지)
  const [shut, setShut] = useState(false);
  // 모바일 메뉴 열림 여부 / 현재 페이지 메뉴(오른쪽에 제목과 하위 항목 표시)
  const [open, setOpen] = useState(false);
  const curCat = MOBILE_MENU.find((m) => m.key === pathname);

  // 메뉴가 열려 있는 동안 배경 스크롤 막고, Esc 로 닫기
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <header className="gnb">
        <div className="wrap">
          <Link to="/" style={{ justifySelf: 'start', display: 'flex' }}>
            <img src="/logo-acxionon-admin.svg" alt="AcXion-On" style={{ height: 20 }} />
          </Link>
          <nav>
            <Link className={on('/')} to="/">
              홈
            </Link>
            <div className={`dd${shut ? " shut" : ""}`}>
              <Link className={on('/product')} to="/product" onMouseEnter={() => setShut(false)} onClick={(e) => { e.currentTarget.blur(); setShut(true); }}>
                제품소개
                <svg className="i">
                  <use href="#chev" />
                </svg>
              </Link>
              <div className="ddm">
                <div>
                  {PRODUCT_MENU.map((m) => (
                    <Link key={m.hash} to={`/product${m.hash}`} onClick={(e) => { e.currentTarget.blur(); setShut(true); }}>
                      {m.title}
                      <span>{m.desc}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link className={on('/contact')} to="/contact">
              문의하기
            </Link>
          </nav>
          <div className="r">
            <Link className="lnk" to="/login" onClick={() => { document.title = 'AcXion-On'; }}>
              로그인
            </Link>
            <Link className="btn o sm" to="/contact">
              문의하기
            </Link>
            <Link className="btn p sm" to="/contact">
              무료 체험
            </Link>
          </div>
          <button type="button" className="mbtn" aria-label="메뉴 열기" onClick={() => setOpen(true)}>
            <i />
            <i />
            <i />
          </button>
        </div>
      </header>

      {/* 모바일 메뉴 (1024px 이하에서만 표시). .gnb 의 backdrop-filter 안에서는 fixed 가 헤더에 갇혀서 header 밖에 둠 */}
      <div className={`mnav${open ? ' on' : ''}`} aria-hidden={!open}>
        <div className="dim" onClick={() => setOpen(false)} />
        <div className="pnl" role="dialog" aria-label="전체 메뉴">
          <div className="top">
            <span className="uic">
              <svg viewBox="0 0 24 24">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 3.6-6 8-6s8 2 8 6" />
              </svg>
            </span>
            <p>
              <Link to="/login" onClick={() => setOpen(false)}>
                로그인
              </Link>
              을 해주세요
            </p>
            <button type="button" className="cls" aria-label="메뉴 닫기" onClick={() => setOpen(false)}>
              <svg className="i">
                <use href="#x" />
              </svg>
            </button>
          </div>
          <div className="bd">
            <ul className="l1">
              {MOBILE_MENU.map((m) => (
                <li key={m.key}>
                  <Link
                    to={m.key}
                    className={on(m.key)}
                    onClick={() => !m.items.length && setOpen(false)}
                  >
                    {m.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="l2">
              {curCat && (
                <>
                  <b>{curCat.label}</b>
                  {curCat.items.length > 0 && (
                    <ul>
                      {curCat.items.map((it) => (
                        <li key={it.to}>
                          <Link to={it.to} onClick={() => setOpen(false)}>
                            {it.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="mft">
            <Link className="btn o" to="/contact" onClick={() => setOpen(false)}>
              문의하기
            </Link>
            <Link className="btn p" to="/contact" onClick={() => setOpen(false)}>
              무료 체험
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
